// Static template + format constants extracted from composerCore.ts.
// Pure data only; no DB or async logic lives here.
import type { ContentDestinationFormat } from "@/lib/composerCore";

export const contentDestinationFormatLabels: Record<ContentDestinationFormat, string> = {
  markdown: "Markdown",
  html: "HTML",
  plaintext: "Plain text",
  newsletter: "Newsletter",
  social_short: "Social — short",
  social_thread: "Social — thread",
  book_chapter: "Book chapter",
};

export const contentDestinationFormatExtension: Record<ContentDestinationFormat, string> = {
  markdown: "md",
  html: "html",
  plaintext: "txt",
  newsletter: "md",
  social_short: "txt",
  social_thread: "txt",
  book_chapter: "md",
};

export const getDefaultComposerTemplate = () => ({
  framework: {
    name: "Topic Tree starter",
    description: "A four-column progression starter you can rename, simplify, or replace. Edit any column, key, principle, or topic to fit your own framework.",
    column_count: 4,
    template_key: "wisdom-pyramid-aethyrius",
  },
  columns: [
    {
      label: "Information",
      description: "What you need to know for productive experience.",
      position: 0,
      color_key: "sky",
      progression_key_label: "Personal Development",
      progression_key_description: "Orient people into grounded, practical contact with the work.",
      principles: ["Embracing Autonomy", "Finding Spiritual Purpose"],
      topics: [
        "Removing Superstition from the Mysteries",
        "Acceptance of Radical Spiritual Evolution",
        "Setting Realistic Expectations",
        "Commitment to Practical Work",
        "Aspirational Advice",
      ],
    },
    {
      label: "Knowledge",
      description: "Making that information useful on practical levels.",
      position: 1,
      color_key: "sage",
      progression_key_label: "Personal Empowerment",
      progression_key_description: "Help users translate insight into stable practice and direction.",
      principles: ["Evolutionary Spirituality", "Practical Initiatory Systems"],
      topics: [
        "Coming into the Genuine Mysteries",
        "Reclaiming Direct Divine Experience",
        "Understanding Allegory in Initiatory Systems",
        "Living into the Mysteries in the Modern World",
      ],
    },
    {
      label: "Understanding",
      description: "Integrating that knowledge with deeper practices.",
      position: 2,
      color_key: "amber",
      progression_key_label: "Spiritual Alignment",
      progression_key_description: "Tie systems, practices, and meaning together without losing practical grounding.",
      principles: ["Practical (Applied) Philosophy", "Householder Balance"],
      topics: [
        "Making Evolutionary Spirituality Practical",
        "Understanding Alchemy",
        "Engaging in Initiatory Theurgy",
        "Understanding the Mechanics of Theurgy",
        "Practical Alchemy",
      ],
    },
    {
      label: "Wisdom",
      description: "Deepening work based on adept understanding.",
      position: 3,
      color_key: "plum",
      progression_key_label: "Grounded Transformation",
      progression_key_description: "Support advanced integration, leadership, and higher-order application.",
      principles: ["Living Theurgy (Theophany)", "Becoming Meta"],
      topics: [
        "Recreating Your Life While Living into the Mysteries",
        "Engaging in Adept Theurgy",
        "Mediating Channels of Power",
        "Develop Skills for Applied Psycho-spiritual Evolution",
        "Practical Abundance",
      ],
    },
  ],
  avatars: [
    {
      name: "Ember",
      experienceLevel: "Beginner",
      summary: "The yearning aspirant seeking safe orientation, proof, and reverent guidance.",
      painPoints: [
        "Overwhelmed by conflicting information",
        "Tired of shallow spiritual content",
        "Craves direct connection with the divine",
      ],
      desires: ["A trustworthy path", "Experiential proof", "Language that bridges curiosity and transmission"],
      toneGuidance: "Speak plainly, with reverence and gentle invitation.",
      vocabularyAssumptions: "Define esoteric terms as they appear.",
      messagingPosture: "Orient, reassure, and invite deeper commitment.",
    },
    {
      name: "Nova",
      experienceLevel: "Intermediate",
      summary: "The disciplined seeker who needs recalibration, integration, and real initiatory structure.",
      painPoints: ["Plateaued growth", "Disillusionment with shallow branding", "Difficulty integrating openings"],
      desires: ["Transmission from real work", "Clear stages", "Tools that cut through illusion"],
      toneGuidance: "Speak with clarity, gravitas, and challenge-with-respect.",
      vocabularyAssumptions: "Can handle initiatory language without constant unpacking.",
      messagingPosture: "Refine, recalibrate, and redirect toward practice.",
    },
    {
      name: "Rowan",
      experienceLevel: "Advanced",
      summary: "The silent initiate looking for peer-level depth, communion, and advanced nourishment.",
      painPoints: ["Isolation", "Ceilings in prior systems", "Questions of surrender and next steps"],
      desires: ["Peer communion", "Greater initiatory alignment", "Higher-order gnosis and sacrament"],
      toneGuidance: "Speak esoterically, directly, and without dilution.",
      vocabularyAssumptions: "Assume shared symbolic and initiatory fluency.",
      messagingPosture: "Offer challenge, communion, and depth.",
    },
  ],
});
